/**
 * feature/intelligence — the TanStack Query wrappers over `lib/api.ts`'s
 * `intelligence/…` fetchers (design §1.4, §6).
 *
 * A thin layer, deliberately: every fetcher, type and query key already lives in
 * `lib/api.ts` beside every other endpoint this app calls, so a future UC2/UC3 pane or the
 * settings section reads the same cache entry a UC1 pane already filled. What lives here is
 * only the `enabled`/`useCase ?? ''` plumbing that would otherwise be six copies of the same
 * three lines across `Uc1.tsx`, `Methodology.tsx` and `useUnitLink.ts`.
 */
import { useQuery } from '@tanstack/react-query';

import {
  fetchIntelCompare,
  fetchIntelConfig,
  fetchIntelDefinition,
  fetchIntelDiagnosis,
  fetchIntelMethodology,
  fetchIntelSeries,
  fetchIntelSimilar,
  fetchIntelSummary,
  fetchIntelUc3Config,
  fetchIntelUnitDetail,
  fetchIntelUnits,
  fetchIntelUseCases,
  queryKeys,
} from '../../lib/api';
import type { Country } from '../../lib/state';

export function useUseCases() {
  return useQuery({ queryKey: queryKeys.intelUseCases(), queryFn: () => fetchIntelUseCases() });
}

export function useIntelSummary(useCase: string | null) {
  return useQuery({
    queryKey: queryKeys.intelSummary(useCase ?? ''),
    queryFn: () => fetchIntelSummary(useCase as string),
    enabled: Boolean(useCase),
  });
}

export function useIntelUnits(useCase: string | null, level: string) {
  return useQuery({
    queryKey: queryKeys.intelUnits(useCase ?? '', level),
    queryFn: () => fetchIntelUnits(useCase as string, level),
    enabled: Boolean(useCase),
  });
}

export function useIntelUnitDetail(useCase: string | null, unitId: string | null) {
  return useQuery({
    queryKey: queryKeys.intelUnit(useCase ?? '', unitId ?? ''),
    queryFn: () => fetchIntelUnitDetail(useCase as string, unitId as string),
    enabled: Boolean(useCase && unitId),
  });
}

export function useIntelMethodology(useCase: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.intelMethodology(useCase ?? ''),
    queryFn: () => fetchIntelMethodology(useCase as string),
    enabled: Boolean(useCase) && enabled,
  });
}

export function useIntelConfig(useCase: string | null) {
  return useQuery({
    queryKey: queryKeys.intelConfig(useCase ?? ''),
    queryFn: () => fetchIntelConfig(useCase as string),
    enabled: Boolean(useCase),
  });
}

/* -------------------------------------------------------------- Task 7: UC2's own reads */

export function useIntelSimilar(
  useCase: string | null,
  unitId: string | null,
  k: number,
  group: string | null,
) {
  return useQuery({
    queryKey: queryKeys.intelSimilar(useCase ?? '', unitId ?? '', k, group),
    queryFn: () => fetchIntelSimilar(useCase as string, unitId as string, k, group),
    enabled: Boolean(useCase && unitId),
  });
}

export function useIntelCompare(useCase: string | null, a: string | null, b: string | null) {
  return useQuery({
    queryKey: queryKeys.intelCompare(useCase ?? '', a ?? '', b ?? ''),
    queryFn: () => fetchIntelCompare(useCase as string, a as string, b as string),
    enabled: Boolean(useCase && a && b),
  });
}

/* -------------------------------------------------------------- Task 7: UC3's own reads */

export function useIntelDiagnosis(crop: string | null, year: number | null, country: Country | null) {
  return useQuery({
    queryKey: queryKeys.intelDiagnosis(crop ?? '', year ?? 0, country),
    queryFn: () => fetchIntelDiagnosis(crop as string, year as number, country),
    enabled: Boolean(crop && year),
  });
}

export function useIntelSeries(unit: string | null, year: number | null) {
  return useQuery({
    queryKey: queryKeys.intelSeries(unit ?? '', year ?? 0),
    queryFn: () => fetchIntelSeries(unit as string, year as number),
    enabled: Boolean(unit && year),
  });
}

export function useIntelUc3Config() {
  return useQuery({ queryKey: queryKeys.intelUc3Config(), queryFn: () => fetchIntelUc3Config() });
}

/** Amendment 1 §3.3: the stepper's own definition — `steps`, `scenarios`, the rainfall policy. */
export function useIntelDefinition(useCase: string) {
  return useQuery({
    queryKey: queryKeys.intelDefinition(useCase),
    queryFn: () => fetchIntelDefinition(useCase),
  });
}
